import React, { useCallback, useEffect, useRef, useState } from "react";
import { useReadMemory } from "../../../generic/hooks/useReadMemory";
import MemoryType from "../../../../service/memory/enum/MemoryType";
import {
  Collapse,
  Divider,
  List,
  notification,
  Popconfirm,
  Progress,
  Space,
  Spin,
} from "antd";
import BackButton from "../../../generic/elements/backbutton/BackButton";
import "./traininghomepagecomponent.css";
import {
  deleteTrainingItem,
  deleteTrainingModule,
  getTrainingModules,
} from "../../../articles/StrapiService";
import { getApiUrl } from "../../../../service/network/UrlMapper";
import SmartIcon from "../../../../components/generic/genericIcon/SmartIcon";
import ReactMarkdown from "react-markdown";
import ErrorNotificationComponent from "../../../../components/generic/error/ErrorNotificationComponent";
import { useGetOrgIdentifier } from "../../../generic/hooks/useGetOrgIdentifier";
import { Fab } from "@mui/material";
import { Add } from "@mui/icons-material";
import TrainingModuleFormModal from "./TrainingModuleFormModal";
import { DownOutlined } from "@ant-design/icons";
import TrainingItemFormModal from "./TrainingItemFormModal";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../../utils/configurationconstants";

const { Panel } = Collapse;

const DOCUMENT_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".ppt",
  ".pptx",
];

const isDocumentFile = (url) => {
  if (!url) return false;
  const path = url.toLowerCase().split("?")[0];
  return DOCUMENT_EXTENSIONS.some((ext) => path.endsWith(ext));
};

const TrainingHomePageComponent = () => {
  const userJson = useReadMemory(MemoryType.USER);
  const organizationIdentifier = useGetOrgIdentifier();
  const configuration = useRecoilValue(currentConfiguration);
  const [traingModules, setTrainingModules] = useState([]);
  const [videoProgressMap, setVideoProgressMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [showTrainingItem, setShowTrainingItem] = useState(false);
  const [moduleId, setModuleId] = useState("");
  const [trainingItemId, setTrainingItemId] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isUpdateAllowed, setIsUpdateAllowed] = useState(false);
  const videoRef = useRef(null);

  const handleTimeUpdate = (id, currentTime, duration) => {
    setVideoProgressMap((prev) => ({
      ...prev,
      [id]: {
        currentTime,
        duration,
        percent: (currentTime / duration) * 100,
      },
    }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const handleDeleteModule = async (id) => {
    setDeleteLoading(true);
    const response = await deleteTrainingModule(id);
    if (response?.status === 200) {
      getModules();
      notification.success({
        message: "Training module deleted successfully!",
        placement: "bottomRight",
      });
    } else {
      notification.error({
        message: "Something went wrong. Please try again.",
        placement: "bottomRight",
      });
    }
    setDeleteLoading(false);
  };

  const handleEditModule = async (id) => {
    if (id) {
      setModuleId(id);
      setShowModuleForm(true);
    }
  };

  const handleAddItem = async (id) => {
    if (id) {
      setModuleId(id);
      setShowTrainingItem(true);
    }
  };

  const getModuleActions = (id) => (
    <Space>
      <span
        onClick={(e) => {
          e.stopPropagation();
          handleAddItem(id);
        }}
      >
        <SmartIcon iconKey="add" width={20} height={20} />
      </span>
      <span
        onClick={(e) => {
          e.stopPropagation();
          handleEditModule(id);
        }}
      >
        <SmartIcon iconKey="edit" width={20} height={20} />
      </span>
      <Popconfirm
        title="Are you sure to delete this Module?"
        placement="left"
        onConfirm={() => handleDeleteModule(id)}
        okText="Confirm"
        cancelText="Cancel"
        overlayClassName="article-delete-popconfirm"
        okButtonProps={{ loading: deleteLoading }}
        onCancel={(e) => e.stopPropagation()}
      >
        <span onClick={(e) => e.stopPropagation()}>
          <SmartIcon iconKey="delete" width={20} height={20} />
        </span>
      </Popconfirm>
    </Space>
  );

  const handleEditItem = async (id) => {
    if (id) {
      setTrainingItemId(id);
      setShowTrainingItem(true);
    }
  };

  const handleDeleteItem = async (id) => {
    setDeleteLoading(true);
    const response = await deleteTrainingItem(id);
    if (response?.status === 200) {
      getModules();
      notification.success({
        message: "Training item deleted successfully!",
        placement: "bottomRight",
      });
    } else {
      notification.error({
        message: "Something went wrong. Please try again.",
        placement: "bottomRight",
      });
    }
    setDeleteLoading(false);
  };

  const getItemActions = (id) => (
    <Space>
      <SmartIcon
        iconKey="edit"
        width={20}
        height={20}
        handleIconClick={() => handleEditItem(id)}
      />

      <Popconfirm
        title="Are you sure to delete this Item?"
        placement="left"
        onConfirm={() => handleDeleteItem(id)}
        okText="Confirm"
        cancelText="Cancel"
        overlayClassName="article-delete-popconfirm"
        okButtonProps={{
          loading: deleteLoading,
        }}
        onCancel={(e) => e.stopPropagation()}
      >
        <span onClick={(e) => e.stopPropagation()}>
          <SmartIcon iconKey="delete" width={20} height={20} />
        </span>
      </Popconfirm>
    </Space>
  );

  const getModules = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getTrainingModules(organizationIdentifier);
      setTrainingModules(data || []);
    } catch (error) {
      ErrorNotificationComponent(error);
    } finally {
      setLoading(false);
    }
  }, [organizationIdentifier]);

  useEffect(() => {
    getModules();
  }, [getModules]);

  useEffect(() => {
    const configurationShowUpdateIcons =
      configuration[ConfigurationConstants.FEATUREACCESS_TRAINING_UPDATES];
    setIsUpdateAllowed(configurationShowUpdateIcons?.visibility);
  }, [configuration]);

  return (
    <div style={{ margin: "20px" }}>
      <div className="form-renderer-back-button">
        <BackButton />
        <p>Training</p>
      </div>
      <section className="training-section-container">
        {loading ? (
          <Spin size="large" className="spinnerCenter" />
        ) : (
          <>
            {traingModules?.map((module) => {
              const isModulesAllowed =
                module?.attributes?.training_roles?.data?.some(
                  (item) => item?.attributes?.role === userJson?.jobTitle
                );
              const trainingItems =
                module?.attributes?.training_items?.data?.sort(
                  (a, b) => a.attributes.sequenceId - b.attributes.sequenceId
                );
              return (
                isModulesAllowed && (
                  <Collapse
                    bordered={false}
                    expandIconPosition="end"
                    className="site-collapse-custom-collapse"
                    expandIcon={({ isActive }) => (
                      <DownOutlined
                        rotate={isActive ? 180 : 0}
                        style={{
                          fontSize: 16,
                          cursor: "pointer",
                          color: "var(--secondary-theme-color)",
                        }}
                      />
                    )}
                  >
                    <Panel
                      header={
                        <div className="d-flex align-items-center justify-content-between">
                          <span>{`${module?.attributes?.sequenceId}. ${module?.attributes?.title}`}</span>
                          {isUpdateAllowed &&
                            module?.attributes?.organizationIdentifier ===
                              organizationIdentifier &&
                            getModuleActions(module?.id)}
                        </div>
                      }
                      key={module?.id}
                      className="site-collapse-custom-panel"
                    >
                      <Divider className="training-content-divider" />
                      <List
                        dataSource={trainingItems}
                        renderItem={(item) => {
                          const isTrainingItemAllowed =
                            item?.attributes?.training_roles?.data?.some(
                              (item) =>
                                item?.attributes?.role === userJson?.jobTitle
                            );
                          const url =
                            item?.attributes?.video?.data?.attributes?.url.trim();
                          const fullUrl = `${getApiUrl()["strapi_url"]}${url}`;
                          const isDocument = isDocumentFile(url);
                          const videoId = item?.attributes?.video?.data?.id;
                          const {
                            currentTime = 0,
                            duration = 0,
                            percent = 0,
                          } = videoProgressMap[videoId] || {};

                          return (
                            isTrainingItemAllowed && (
                              <List.Item
                                key={item.id}
                                className="list-item-content"
                              >
                                <List.Item.Meta
                                  className="list-item-meta"
                                  avatar={
                                    <SmartIcon
                                      iconKey={`${
                                        isDocument ? "document" : "video_icon"
                                      }`}
                                    />
                                  }
                                  title={
                                    <div className="d-flex align-items-center justify-content-between">
                                      <span>{`${item?.attributes?.sequenceId}. ${item?.attributes?.title}`}</span>
                                      {isUpdateAllowed &&
                                        item?.attributes
                                          ?.organizationIdentifier ===
                                          organizationIdentifier &&
                                        getItemActions(item?.id)}
                                    </div>
                                  }
                                  description={
                                    <>
                                      <ReactMarkdown>
                                        {item?.attributes?.description}
                                      </ReactMarkdown>
                                      {url &&
                                        (isDocument ? (
                                          <a
                                            href={fullUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="open-pdf"
                                          >
                                            Open Document
                                          </a>
                                        ) : (
                                          <video
                                            ref={videoRef}
                                            controls
                                            onTimeUpdate={(e) =>
                                              handleTimeUpdate(
                                                videoId,
                                                e.target.currentTime,
                                                e.target.duration
                                              )
                                            }
                                            style={{
                                              marginTop: 8,
                                              borderRadius: 6,
                                              width: 280,
                                              height: "auto",
                                            }}
                                          >
                                            <source
                                              src={fullUrl}
                                              type="video/mp4"
                                            />
                                            Sorry, your browser doesn’t support
                                            embedded videos.
                                          </video>
                                        ))}
                                    </>
                                  }
                                />
                                {url && !isDocument && (
                                  <div className="progress-wrapper">
                                    <div className="progress-bar-details">
                                      <span>
                                        {Math.floor(percent)}% Completed
                                      </span>
                                      <span>
                                        {formatTime(currentTime)} /{" "}
                                        {formatTime(duration)}
                                      </span>
                                    </div>
                                    <Progress
                                      percent={Math.floor(percent)}
                                      showInfo={false}
                                      strokeColor="var(--main-theme-color)"
                                      trailColor="#E3E3E3"
                                    />
                                  </div>
                                )}
                              </List.Item>
                            )
                          );
                        }}
                      />
                    </Panel>
                  </Collapse>
                )
              );
            })}
          </>
        )}

        {isUpdateAllowed && (
          <Fab
            className="view-forms-button"
            onClick={() => setShowModuleForm(!showModuleForm)}
          >
            <Add className="icon-color" />
          </Fab>
        )}

        {showModuleForm && (
          <>
            <TrainingModuleFormModal
              showModuleForm={showModuleForm}
              setShowModuleForm={setShowModuleForm}
              getModules={getModules}
              moduleId={moduleId}
              setModuleId={setModuleId}
            />
          </>
        )}
        {showTrainingItem && (
          <>
            <TrainingItemFormModal
              showTriningItem={showTrainingItem}
              setShowTrainingItem={setShowTrainingItem}
              getModules={getModules}
              trainingItemId={trainingItemId}
              setTrainingItemId={setTrainingItemId}
              moduleId={moduleId}
              setModuleId={setModuleId}
            />
          </>
        )}
      </section>
    </div>
  );
};

export default TrainingHomePageComponent;
